// /admin/links (architecture sprint-05 §6.7; sprint-06 §6.3, S11.3). Lists every link with its
// visibility and status; per-row actions come from the shared state machine (ShareLinkActions). Copy
// decrypts server-side and is offered on active links only. Sprint 17: a Report column says what each link
// covers ("All projects", "Alpha", "3 projects · 2 members"; names only), the full lists in its title.
import { useState } from 'react';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { LinkRevealDialog, type RevealedLink } from '@/components/LinkRevealDialog';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMe } from '@/hooks/use-me';
import { useCopyShareLink, useShareLinks } from '@/hooks/use-share-links';
import { ApiClientError } from '@/lib/api';
import { copyText } from '@/lib/clipboard';
import { formatDateTime, formatRange } from '@/lib/format';
import { GENERIC_ERROR_MESSAGE } from '@/lib/query-client';
import { shareLinkScopeDetail, shareLinkScopeLabel } from '@/lib/share-link-scope';
import { useToday } from '@/lib/use-now';
import type { ShareLink, ShareLinkStatus } from '@/types/admin';
import { ShareLinkActions } from './ShareLinkActions';

export const SHARED_LINKS_INTRO =
  'Links are created from the Team report and open for anyone with the link, showing only that report. Make private to require sign-in. Revoking is permanent.';

const STATUS: Record<ShareLinkStatus, { label: string; variant: BadgeProps['variant'] }> = {
  active: { label: 'Active', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
  revoked: { label: 'Revoked', variant: 'danger' },
};
const VISIBILITY: Record<ShareLink['visibility'], { label: string; variant: BadgeProps['variant'] }> = {
  sign_in_required: { label: 'Sign-in', variant: 'info' },
  public: { label: 'Public', variant: 'warning' },
};
const INACTIVE_CODE = 'SHARE_LINK_INACTIVE';

function ScopeText({ link }: { link: ShareLink }): JSX.Element {
  return <span title={shareLinkScopeDetail(link) ?? undefined}>{shareLinkScopeLabel(link)}</span>;
}

function StatusBadge({ status }: { status: ShareLinkStatus }): JSX.Element {
  return <Badge variant={STATUS[status].variant}>{STATUS[status].label}</Badge>;
}

function VisibilityBadge({ visibility }: { visibility: ShareLink['visibility'] }): JSX.Element {
  return <Badge variant={VISIBILITY[visibility].variant}>{VISIBILITY[visibility].label}</Badge>;
}

export function SharedLinksPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="5xl">
        <Skeleton className="h-40 w-full" />
      </Page>
    );
  return <SharedLinks timezone={me.timezone} />;
}

function SharedLinks({ timezone }: { timezone: string }): JSX.Element {
  const today = useToday(timezone);
  const links = useShareLinks();
  const copy = useCopyShareLink();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealedLink | null>(null);

  function onCopy(link: ShareLink): void {
    setPendingId(link.id);
    copy.mutate(link.id, {
      onSuccess: async ({ url }) => {
        if (await copyText(url)) toast.success('Link copied.');
        else setRevealed({ link: url, title: 'Share link', inputLabel: 'Share link', description: 'Copy this link and share it yourself.' });
      },
      onError: (err) => {
        toast.error(err instanceof ApiClientError ? err.message : GENERIC_ERROR_MESSAGE);
        if (err instanceof ApiClientError && err.code === INACTIVE_CODE) void links.refetch();
      },
      onSettled: () => setPendingId(null),
    });
  }

  function actions(link: ShareLink): JSX.Element {
    return <ShareLinkActions link={link} today={today} timezone={timezone} copyPending={pendingId === link.id} onCopy={onCopy} />;
  }

  return (
    <Page width="5xl">
      <PageHeader title="Shared links" subtitle={SHARED_LINKS_INTRO} />
      {!links.data ? (
        <Skeleton className="h-40 w-full" />
      ) : links.data.length === 0 ? (
        <EmptyState icon={Link2} title="No links yet. Create one from the Team report." />
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Range</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Created by</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.data.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell className="whitespace-nowrap">{formatRange(link.from, link.to)}</TableCell>
                    <TableCell>
                      <ScopeText link={link} />
                    </TableCell>
                    <TableCell>{link.createdBy.name}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(link.createdAt)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(link.expiresAt)}</TableCell>
                    <TableCell>
                      <VisibilityBadge visibility={link.visibility} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={link.status} />
                    </TableCell>
                    <TableCell className="text-right">{actions(link)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-3 md:hidden">
            {links.data.map((link) => (
              <li key={link.id} className="card space-y-2 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{formatRange(link.from, link.to)}</span>
                  <span className="flex gap-1">
                    <VisibilityBadge visibility={link.visibility} />
                    <StatusBadge status={link.status} />
                  </span>
                </div>
                <p>
                  <ScopeText link={link} />
                </p>
                <p className="text-muted-foreground">
                  {link.createdBy.name} · {formatDateTime(link.createdAt)} · expires {formatDateTime(link.expiresAt)}
                </p>
                {actions(link)}
              </li>
            ))}
          </ul>
        </>
      )}
      <LinkRevealDialog value={revealed} onClose={() => setRevealed(null)} />
    </Page>
  );
}
